import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from 'components/card';
import GoBack from 'components/actions/GoBack';
import ConfirmModal from 'components/modals/ConfirmModal';
import ShopifyMenu from 'views/shopify/components/ShopifyMenu';
import SearchIcon from 'components/icons/SearchIcon';
import SearchSelector from 'components/fields/SearchSelector';
import fetchClient from 'interfaces/fetchClient';
import { searchProductsForSelect } from 'interfaces/products';
import env from 'variables/config';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TABS: { key: string; label: string }[] = [
    { key: '', label: 'Tous' },
    { key: 'unmatched', label: 'Non liés' },
    { key: 'duplicate', label: 'Doublons' },
    { key: 'matched', label: 'Liés' },
    { key: 'ignored', label: 'Ignorés' },
];

const STATUS_BADGE: Record<string, string> = {
    matched: 'bg-green-100 text-green-700',
    unmatched: 'bg-amber-100 text-amber-700',
    duplicate: 'bg-red-100 text-red-700',
    ignored: 'bg-gray-100 text-gray-500',
};

// Shopify's own publication status for the product — distinct from the
// reconciliation STATUS_BADGE above. Active products are prioritized during
// auto-matching when a SKU is shared with a draft/archived duplicate.
const SHOPIFY_STATUS_LABEL: Record<string, string> = {
    active: 'Actif',
    draft: 'Brouillon',
    archived: 'Archivé',
};
const SHOPIFY_STATUS_BADGE: Record<string, string> = {
    active: 'bg-green-50 text-green-600',
    draft: 'bg-gray-100 text-gray-500',
    archived: 'bg-gray-100 text-gray-400',
};

const CreateProductModal = ({
    link,
    onClose,
    onCreated,
}: {
    link: any;
    onClose: () => void;
    onCreated: () => void;
}) => {
    const [form, setForm] = useState({
        name: link.title + (link.variantTitle ? ` — ${link.variantTitle}` : ''),
        ref: link.sku || '',
        price: '',
        purchasePrice: '',
        vat: '20',
        unity: 'unité',
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);
        try {
            await fetchClient.post(
                `${env.apiUrl}/shopify/products/${link._id}/create-product`,
                {
                    name: form.name,
                    ref: form.ref,
                    price: form.price ? Number(form.price) : undefined,
                    purchasePrice: form.purchasePrice ? Number(form.purchasePrice) : undefined,
                    vat: Number(form.vat) || 20,
                    unity: form.unity,
                },
                authHeaders(),
            );
            toast.success('Produit Toola créé et lié.');
            onCreated();
            onClose();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Erreur lors de la création du produit.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-navy-800">
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Créer un produit Toola</h3>
                <p className="mt-1 text-xs text-gray-500">
                    Ce produit sera créé dans Toola et automatiquement lié à la variante Shopify.
                </p>
                <div className="mt-4 flex flex-col gap-3">
                    <div>
                        <label className="text-xs font-semibold text-gray-500">Nom</label>
                        <input
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-gray-500">Référence</label>
                            <input
                                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none"
                                value={form.ref}
                                onChange={(e) => setForm({ ...form, ref: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-500">Unité</label>
                            <input
                                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none"
                                value={form.unity}
                                onChange={(e) => setForm({ ...form, unity: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-gray-500">Prix de vente</label>
                            <input
                                type="number"
                                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none"
                                value={form.price}
                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-500">Prix d'achat</label>
                            <input
                                type="number"
                                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none"
                                value={form.purchasePrice}
                                onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-gray-500">TVA %</label>
                            <input
                                type="number"
                                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none"
                                value={form.vat}
                                onChange={(e) => setForm({ ...form, vat: e.target.value })}
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-3">
                    <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100">
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || !form.name}
                        className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                    >
                        {saving ? 'Création...' : 'Créer et lier'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ShopifyProducts = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeStatus = searchParams.get('status') || '';
    const [items, setItems] = useState<any[]>([]);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(true);
    const [creatingFor, setCreatingFor] = useState<any | null>(null);
    const [aligningId, setAligningId] = useState<string | null>(null);
    const [alignConfirmLink, setAlignConfirmLink] = useState<any | null>(null);
    const [search, setSearch] = useState('');
    const latestRequestId = useRef(0);

    const fetchItems = useCallback(() => {
        setLoading(true);
        const requestId = ++latestRequestId.current;
        const params: any = { limit: 200 };
        if (activeStatus) params.status = activeStatus;
        fetchClient
            .get(`${env.apiUrl}/shopify/products`, { ...authHeaders(), params })
            .then((res) => {
                // Ignore this response if a newer request has since been fired
                // (e.g. rapid tab switching) — otherwise a slow older response
                // could resolve last and overwrite the screen with stale data.
                if (requestId !== latestRequestId.current) return;
                setItems(res.data.items || []);
                setCounts(res.data.counts || {});
            })
            .catch(() => {
                if (requestId !== latestRequestId.current) return;
                toast.error('Erreur lors du chargement des produits Shopify.');
            })
            .finally(() => {
                if (requestId !== latestRequestId.current) return;
                setLoading(false);
            });
    }, [activeStatus]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const matchProduct = async (link: any, product: any) => {
        try {
            const res = await fetchClient.post(
                `${env.apiUrl}/shopify/products/${link._id}/match`,
                { productId: product._id },
                authHeaders(),
            );
            const updated = res.data.updatedOrders || 0;
            toast.success(`Produit lié à "${product.name}".${updated ? ` ${updated} commande(s) mise(s) à jour.` : ''}`);
            fetchItems();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Erreur lors du rapprochement.');
        }
    };

    const ignoreProduct = async (link: any) => {
        try {
            await fetchClient.post(`${env.apiUrl}/shopify/products/${link._id}/ignore`, {}, authHeaders());
            toast.success('Produit ignoré.');
            fetchItems();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Erreur lors de l'ignore.");
        }
    };

    const confirmAlignStock = async () => {
        const link = alignConfirmLink;
        if (!link) return;
        setAligningId(link._id);
        try {
            const res = await fetchClient.post(`${env.apiUrl}/shopify/products/${link._id}/align-stock`, {}, authHeaders());
            toast.success(res.data.changed ? `Stock Toola aligné à ${res.data.quantity}.` : 'Les stocks étaient déjà alignés.');
            fetchItems();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Erreur lors de l'alignement du stock.");
        } finally {
            setAligningId(null);
        }
    };

    const searchLower = search.trim().toLowerCase();
    const filteredItems = searchLower
        ? items.filter((link: any) =>
              [link.title, link.variantTitle, link.sku, link.matchedProductId?.name, link.matchedProductId?.ref]
                  .filter(Boolean)
                  .some((field: string) => field.toLowerCase().includes(searchLower)),
          )
        : items;

    return (
        <>
            <GoBack />
            <ShopifyMenu />
            <Card extra="w-full h-full sm:overflow-auto p-5 border mt-5">
                <div className="mb-5">
                    <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Produits Shopify</h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Rapprochez les produits/variantes Shopify avec vos produits Toola. Les ventes Shopify ne
                        décrémentent le stock que sur les produits liés. Le stock Toola n'est jamais recalé
                        automatiquement sur celui de Shopify — un écart s'affiche en orange avec un bouton pour
                        l'aligner manuellement si besoin. En cas de SKU partagé par plusieurs variantes Shopify, le
                        produit <strong>Actif</strong> est toujours priorisé sur un doublon en Brouillon/Archivé.
                    </p>
                </div>

                <div className="mb-5 flex flex-wrap gap-2">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setSearchParams(tab.key ? { status: tab.key } : {})}
                            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                                activeStatus === tab.key
                                    ? 'bg-navy-700 text-white dark:bg-white dark:text-navy-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {tab.label}
                            {tab.key && typeof counts[tab.key] === 'number' ? ` (${counts[tab.key]})` : ''}
                        </button>
                    ))}
                </div>

                <div className="mb-5 flex h-[38px] max-w-md flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white">
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Rechercher par nom, variante, SKU ou produit Toola lié..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                    />
                </div>

                {loading && <div className="py-10 text-center text-gray-500">Chargement...</div>}
                {!loading && items.length === 0 && (
                    <div className="py-10 text-center text-gray-500">Aucun produit dans cette catégorie.</div>
                )}
                {!loading && items.length > 0 && filteredItems.length === 0 && (
                    <div className="py-10 text-center text-gray-500">Aucun produit trouvé pour "{search}".</div>
                )}

                {!loading && filteredItems.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full text-left text-sm text-gray-500">
                            <thead className="bg-gray-50 text-gray-700">
                                <tr>
                                    <th className="px-4 py-3">Produit Shopify</th>
                                    <th className="px-4 py-3">SKU</th>
                                    <th className="px-4 py-3">Stock</th>
                                    <th className="px-4 py-3">Statut</th>
                                    <th className="px-4 py-3">Rapprochement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((link: any) => (
                                    <tr key={link._id} className="border-b bg-white hover:bg-gray-50 dark:border-navy-700 dark:bg-navy-800">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {link.imageUrl && (
                                                    <img src={link.imageUrl} alt={link.title} className="h-10 w-10 rounded object-cover" />
                                                )}
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium text-gray-900 dark:text-white">{link.title}</p>
                                                        {link.shopifyStatus && (
                                                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SHOPIFY_STATUS_BADGE[link.shopifyStatus] || 'bg-gray-100 text-gray-500'}`}>
                                                                {SHOPIFY_STATUS_LABEL[link.shopifyStatus] || link.shopifyStatus}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {link.variantTitle && <p className="text-xs text-gray-400">{link.variantTitle}</p>}
                                                    {link.status === 'duplicate' && link.duplicateReason && (
                                                        <p className="mt-1 text-xs text-red-500">{link.duplicateReason}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{link.sku || '-'}</td>
                                        <td className="px-4 py-3">
                                            {link.status === 'matched' && link.matchedProductId ? (
                                                (() => {
                                                    const toolaQty = Number(link.matchedProductId.quantity ?? 0);
                                                    const shopifyQty = link.inventoryQuantity;
                                                    const differs = shopifyQty !== null && shopifyQty !== undefined && toolaQty !== Number(shopifyQty);
                                                    return (
                                                        <div>
                                                            <p className={`font-medium ${differs ? 'text-amber-600' : 'text-gray-700'}`}>
                                                                Toola : {toolaQty}
                                                                {differs && ' ⚠'}
                                                            </p>
                                                            <p className="text-xs text-gray-400">Shopify : {shopifyQty ?? '-'}</p>
                                                            {differs && (
                                                                <button
                                                                    onClick={() => setAlignConfirmLink(link)}
                                                                    disabled={aligningId === link._id}
                                                                    className="mt-1 text-xs font-medium text-amber-600 underline hover:text-amber-700 disabled:opacity-50"
                                                                >
                                                                    {aligningId === link._id ? 'Alignement...' : 'Aligner sur Shopify'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-gray-400">Shopify : {link.inventoryQuantity ?? '-'}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE[link.status]}`}>
                                                {TABS.find((t) => t.key === link.status)?.label || link.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-48">
                                                    <SearchSelector
                                                        hideLabel
                                                        inputSmall
                                                        options={[]}
                                                        displayKey="name"
                                                        secondaryKey="ref"
                                                        defaultKey="_id"
                                                        value={link.matchedProductId?._id || null}
                                                        onSearch={(term) => searchProductsForSelect(term)}
                                                        onSelect={(product: any) => matchProduct(link, product)}
                                                    />
                                                </div>
                                                {link.status !== 'ignored' && (
                                                    <button
                                                        onClick={() => setCreatingFor(link)}
                                                        className="whitespace-nowrap rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                                                    >
                                                        + Créer
                                                    </button>
                                                )}
                                                {link.status !== 'ignored' && (
                                                    <button
                                                        onClick={() => ignoreProduct(link)}
                                                        className="whitespace-nowrap text-xs font-medium text-gray-400 hover:text-gray-600"
                                                    >
                                                        Ignorer
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {creatingFor && (
                <CreateProductModal link={creatingFor} onClose={() => setCreatingFor(null)} onCreated={fetchItems} />
            )}

            <ConfirmModal
                isOpen={!!alignConfirmLink}
                onClose={() => setAlignConfirmLink(null)}
                onConfirm={confirmAlignStock}
                title="Aligner le stock"
                description={
                    alignConfirmLink && (
                        <>
                            Aligner le stock Toola de <strong>{alignConfirmLink.matchedProductId?.name}</strong> sur le
                            stock Shopify (<strong>{alignConfirmLink.inventoryQuantity}</strong>) ?
                            <br />
                            Un mouvement de stock sera enregistré pour tracer ce changement.
                        </>
                    )
                }
                confirmText="Aligner"
                cancelText="Annuler"
                confirmColor="bg-amber-500"
                isWarning
            />
        </>
    );
};

export default ShopifyProducts;
